import { useEffect, useRef, useState, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import type { CaseCardProps } from '../../types/case-management'
import {
  ClientLine,
  Counts,
  PRIORITY_STYLES,
  Progress,
  TitleButton,
  TopRow,
} from './case-card/case-card.parts'
import { isDarkTheme } from './case-card/case-card.helpers'

export function CaseCard({ caseData, onClick }: CaseCardProps): ReactElement {
  const { t } = useTranslation('cases')
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const priority = PRIORITY_STYLES[caseData.priority]
  const dark = isDarkTheme()
  const clientDisplay = caseData.clientShortName ?? caseData.clientName ?? t('card.noClient')

  useEffect(() => {
    function handleClickOutside(event: MouseEvent): void {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleNavigate = (): void => {
    void navigate(`/cases/${caseData.id}`)
  }
  const handleEdit = (event: React.MouseEvent): void => {
    event.stopPropagation()
    setMenuOpen(false)
    onClick?.()
  }
  const handleMenuToggle = (event: React.MouseEvent): void => {
    event.stopPropagation()
    setMenuOpen(!menuOpen)
  }

  return (
    <div
      className="group relative w-full rounded-lg border p-4 text-left transition-all duration-200 hover:shadow-md hover:shadow-lime-500/5"
      style={{ background: 'var(--bg-card)', borderColor: 'var(--card-border, var(--border))' }}
      onMouseEnter={(event) => {
        event.currentTarget.style.borderColor = 'rgb(190, 242, 100)'
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.borderColor = 'var(--card-border, var(--border))'
      }}
    >
      <div className={`absolute top-0 right-0 left-0 h-1 rounded-t-lg ${priority.dot}`} />
      <TopRow
        caseData={caseData}
        priority={priority}
        dark={dark}
        menuRef={menuRef}
        menuOpen={menuOpen}
        onMenuToggle={handleMenuToggle}
        onEdit={handleEdit}
        onCloseMenu={() => {
          setMenuOpen(false)
        }}
        t={t}
      />
      <TitleButton name={caseData.name} onNavigate={handleNavigate} />
      <ClientLine
        clientDisplay={clientDisplay}
        orgCaseReference={caseData.orgCaseReference}
        t={t}
      />
      <Counts
        collectionCount={caseData.collectionCount}
        artifactCount={caseData.artifactCount}
        t={t}
      />
      <Progress completionPercent={caseData.analysisCompletion} t={t} />
    </div>
  )
}
